import { ImageData, ProcessedImageData, AtkinsonOptions } from './esc-pos-image.types';

/**
 * Apply Atkinson dithering to an image
 * Uses error diffusion with a different distribution pattern than Floyd-Steinberg
 * Produces a distinctive look with slightly less contrast
 * @param imageData Input image data
 * @param options Atkinson options with optional threshold
 * @returns Processed black and white image data with Atkinson error diffusion
 */
export function atkinson(imageData: ImageData, options: AtkinsonOptions = {}): ProcessedImageData {
  const { data, width, height } = imageData;
  const threshold = options.threshold || 128;

  // Create working copy of the image data as we'll modify it during processing
  const workingData = new Float32Array(width * height);
  const processedData = new Uint8ClampedArray(data.length);

  // Convert RGBA to grayscale and store in working array
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    // Calculate luminance
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    workingData[i / 4] = luminance;
  }

  // Apply Atkinson dithering
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const oldPixel = workingData[idx];

      // Determine new pixel value (black or white)
      const newPixel = oldPixel >= threshold ? 255 : 0;
      workingData[idx] = newPixel;

      // Calculate error to distribute
      const error = oldPixel - newPixel;

      // Distribute error to neighboring pixels using Atkinson matrix:
      //     * 1/8 1/8
      // 1/8 1/8 1/8
      //     1/8

      // Note: Atkinson uses 1/8 for each neighbor (total 6/8), meaning 2/8 of error is lost
      // This creates the characteristic Atkinson look with less contrast

      // Right pixel
      if (x + 1 < width) {
        workingData[idx + 1] += error / 8;
      }

      // Two pixels to the right
      if (x + 2 < width) {
        workingData[idx + 2] += error / 8;
      }

      // Bottom-left pixel
      if (y + 1 < height && x - 1 >= 0) {
        workingData[idx + width - 1] += error / 8;
      }

      // Bottom pixel
      if (y + 1 < height) {
        workingData[idx + width] += error / 8;
      }

      // Bottom-right pixel
      if (y + 1 < height && x + 1 < width) {
        workingData[idx + width + 1] += error / 8;
      }

      // Two rows down, center pixel
      if (y + 2 < height) {
        workingData[idx + width * 2] += error / 8;
      }

      // Set the processed pixel in RGBA format
      const rgbaIdx = idx * 4;
      processedData[rgbaIdx] = newPixel; // Red
      processedData[rgbaIdx + 1] = newPixel; // Green
      processedData[rgbaIdx + 2] = newPixel; // Blue
      processedData[rgbaIdx + 3] = data[rgbaIdx + 3]; // Keep original alpha
    }
  }

  return {
    data: processedData,
    width,
    height,
  };
}
